import { BarChart3, Eye, type LucideIcon, Phone, User } from 'lucide-react';

import type { VisibilityMetrics } from '@modules/_shared/shared/utils/statistics';
import { formatNumber } from '@modules/_shared/utils/formatting';
import type { Translate } from '@shared/hooks/locale';

type VisibilityMetricDefinition = {
  key: keyof VisibilityMetrics;
  icon: LucideIcon;
  getLabel: (translate: Translate) => string;
  getTooltip?: (translate: Translate) => { title: string; description: string };
  format?: (
    metrics: VisibilityMetrics | undefined,
    locale: string,
  ) => string | number | undefined;
};

export const VISIBILITY_METRICS_DEFINITIONS: VisibilityMetricDefinition[] = [
  {
    key: 'impressions',
    icon: Eye,
    getLabel: t => t('Impressions'),
    getTooltip: t => ({
      title: t('Total Impressions'),
      description: t(
        'Number of times your profile link was displayed to users on RealAdvisor',
      ),
    }),
  },
  {
    key: 'profileViews',
    icon: User,
    getLabel: t => t('Profile views'),
    getTooltip: t => ({
      title: t('Profile views'),
      description: t('Number of users who viewed your agent or agency profile'),
    }),
  },
  {
    key: 'avgPosition',
    icon: BarChart3,
    getLabel: t => t('Avg. Position'),
    getTooltip: t => ({
      title: t('Avg. Position'),
      description: t(
        'The average position of your profile in search results on RealAdvisor',
      ),
    }),
    format: metrics => metrics?.avgPosition,
  },
  {
    key: 'phoneClicks',
    icon: Phone,
    getLabel: t => t('Phone Clicks'),
    getTooltip: t => ({
      title: t('Phone Clicks'),
      description: t(
        'Number of times users clicked your phone number on RealAdvisor',
      ),
    }),
  },
];

export const getVisibilityMetricValue = (
  definition: VisibilityMetricDefinition,
  metrics: VisibilityMetrics | undefined,
  locale: string,
): string | number | undefined => {
  if (definition.format) {
    return definition.format(metrics, locale);
  }

  if (!metrics) {
    return undefined;
  }

  const value = metrics[definition.key];
  return typeof value === 'number' ? formatNumber(value, locale) : undefined;
};

export const getVisibilityMetricCopy = (
  key: keyof VisibilityMetrics,
  translate: Translate,
) => {
  const definition = VISIBILITY_METRICS_DEFINITIONS.find(d => d.key === key);

  return {
    label: definition?.getLabel(translate) ?? '',
    description: definition?.getTooltip?.(translate)?.description ?? '',
  };
};
